import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSession } from "../Login/SessionContext";
import LoadingAnime from "../Common/LoadingAnime";
import { isAdminUser } from "../../utils/userUtils";
import { API_HOST } from "../../constants";
import Pagination from "../ui/Pagination";
import Button from "../ui/Button";
import { getCookie } from "../../utils/cookieUtil";

function Profile() {
  const { user, isLoading } = useSession();
  const [posts, setPosts] = useState([]);
  const [comments, setComments] = useState([]);
  const [postPage, setPostPage] = useState(1);
  const [commentPage, setCommentPage] = useState(1);
  const [postTotalPages, setPostTotalPages] = useState(1);
  const [commentTotalPages, setCommentTotalPages] = useState(1);
  const [postCount, setPostCount] = useState(0);
  const [commentCount, setCommentCount] = useState(0);
  const [loadingLists, setLoadingLists] = useState(true);

  useEffect(() => {
    if (!user) return;
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API_HOST}/api/my/bbs/`, {
          withCredentials: true,
          params: { page: postPage },
        });
        setPosts(response.data.results || []);
        setPostTotalPages(response.data.total_pages || 1);
        setPostCount(response.data.count || 0);
      } catch (error) {
        setPosts([]);
        setPostTotalPages(1);
        setPostCount(0);
      }
    };

    const fetchComments = async () => {
      try {
        const response = await axios.get(`${API_HOST}/api/my/comments/`, {
          withCredentials: true,
          params: { page: commentPage },
        });
        setComments(response.data.results || []);
        setCommentTotalPages(response.data.total_pages || 1);
        setCommentCount(response.data.count || 0);
      } catch (error) {
        setComments([]);
        setCommentTotalPages(1);
        setCommentCount(0);
      }
    };

    const fetchAll = async () => {
      setLoadingLists(true);
      try {
        await Promise.all([fetchPosts(), fetchComments()]);
      } finally {
        setLoadingLists(false);
      }
    };

    fetchAll();
  }, [user, postPage, commentPage]);

  if (isLoading) return <LoadingAnime label="프로필 로딩 중..." />;

  if (!user) {
    return (
      <div className="glass-card p-6">
        <h2 className="text-lg font-semibold text-white">로그인이 필요합니다</h2>
        <p className="mt-2 text-sm text-ink-200">프로필을 보려면 로그인하세요.</p>
      </div>
    );
  }

  const handleDeletePost = async (documentId) => {
    if (!window.confirm("게시글을 삭제할까요?")) return;
    await axios.patch(`${API_HOST}/api/my/bbs/${documentId}/delete/`, {
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCookie("csrftoken"),
        },
        withCredentials: true,
    })
    setPostPage(1);
  }
    
  

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("댓글을 삭제할까요?")) return;
    await axios.patch(`${API_HOST}/api/my/comments/${commentId}/delete/`,{
      headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCookie("csrftoken"),
        },
        withCredentials: true,
    })
    setCommentPage(1);
  }

  return (
    <div className="space-y-6">
      <section className="glass-card p-6">
        <h1 className="font-display text-3xl text-white">{user.nickname || user.username}</h1>
        <p className="mt-2 text-sm text-ink-200">{user.email || "이메일 정보 없음"}</p>
      </section>

      <section className="glass-card p-6">
        <h2 className="text-lg font-semibold text-white">계정 정보</h2>
        <div className="mt-4 grid gap-3 text-sm text-ink-200 sm:grid-cols-2">
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <p className="text-xs uppercase text-ink-200">아이디</p>
            <p className="mt-1 text-white">{user.username}</p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <p className="text-xs uppercase text-ink-200">권한</p>
            <p className="mt-1 text-white">{isAdminUser(user) ? "관리자" : "일반 사용자"}</p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <p className="text-xs uppercase text-ink-200">레벨</p>
            <p className="mt-1 text-white">Lv. {user.level ?? 1}</p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <p className="text-xs uppercase text-ink-200">포인트</p>
            <p className="mt-1 text-white">{user.point ?? 0} pt</p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <p className="text-xs uppercase text-ink-200">작성 글</p>
            <p className="mt-1 text-white">{postCount} 개</p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <p className="text-xs uppercase text-ink-200">작성 댓글</p>
            <p className="mt-1 text-white">{commentCount} 개</p>
          </div>
        </div>
      </section>

      <section className="glass-card p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">내 게시글</h2>
        </div>
        {loadingLists ? (
          <LoadingAnime label="게시글 로딩 중..." />
        ) : posts.length === 0 ? (
          <p className="mt-4 text-sm text-ink-200">작성한 게시글이 없습니다.</p>
        ) : (
          <div className="mt-4 space-y-3 text-sm text-ink-200">
            {posts.map((post) => (
              <div
                key={post.document_id}
                className="flex flex-col gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <a
                  href={`/bbs/${post.board_module_name}/${post.document_id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="block"
                >
                  <p className="text-white">{post.title}</p>
                  <p className="text-xs text-ink-200">{post.category_name || "카테고리 없음"}</p>
                </a>
                <Button variant="secondary" onClick={() => handleDeletePost(post.document_id)}>
                  삭제
                </Button>
              </div>
            ))}
          </div>
        )}
        <div className="mt-4">
          <Pagination totalPages={postTotalPages} currentPage={postPage} onPageChange={setPostPage} />
        </div>
      </section>

      <section className="glass-card p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">내 댓글</h2>
        </div>
        {loadingLists ? (
          <LoadingAnime label="댓글 로딩 중..." />
        ) : comments.length === 0 ? (
          <p className="mt-4 text-sm text-ink-200">작성한 댓글이 없습니다.</p>
        ) : (
          <div className="mt-4 space-y-3 text-sm text-ink-200">
            {comments.map((comment) => (
              <div
                key={comment.id}
                className="flex flex-col gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <a
                  href={comment.board_module_name ? `/bbs/${comment.board_module_name}/${comment.bbs}` : `/bbs/${comment.bbs}`}
                  target="_blank"
                  rel="noreferrer"
                  className="block"
                >
                  <p className="text-white">{comment.content}</p>
                  <p className="text-xs text-ink-200">게시글 ID {comment.bbs}</p>
                </a>
                <Button variant="secondary" onClick={() => handleDeleteComment(comment.id)}>
                  삭제
                </Button>
              </div>
            ))}
          </div>
        )}
        <div className="mt-4">
          <Pagination totalPages={commentTotalPages} currentPage={commentPage} onPageChange={setCommentPage} />
        </div>
      </section>
    </div>
  );
}

export default Profile;
